/* eslint-disable no-undef */
import Config from "./apiUrl";
import { getCookie } from "./storage";
import { getPath } from "../../utils";
import ErrorMsgs from "../../constants/Errors.json";
import { getDashboardUrl, getLogoutUrl } from "../../views/Home";

const _AuthHeaders = (isFormData) => {
  const token = getCookie("_access_token_" + process.env.REACT_APP_ENV);
  const defaultHeaders = {
    Authorization: `${token ? `Bearer ${token}` : ""}`,
    app: "apigw",
    credentials: "include",
  };

  if (isFormData) {
    return defaultHeaders;
  }
  defaultHeaders["Content-Type"] = "application/json";
  return defaultHeaders;
};

const getErrorMessage = (res) => {
  if (getPath(res, "error.message", undefined)) {
    return res.error.message;
  } else if (res.message) {
    return res.message;
  } else if (res.error) {
    return res.error;
  }
  return ErrorMsgs.GENERAL_ERROR;
};

const connectionHandler = () => {
  const isOnline = navigator.onLine;
  if (isOnline) {
    return [true, null];
  }
  return [false, Promise.reject(ErrorMsgs.OFFLINE_ERROR)];
};

const finalUrl = (url) => {
  return url;
};

const isAuthError = (res) => {
  if (res?.status === 401 || res?.status === 403) {
    return true;
  }
  return false;
};

const handleAuthError = () => {
  if (getPath(process.env, "REACT_APP_LOGOUT_URL", undefined)) {
    window.location.assign(getLogoutUrl());
  } else {
    window.location.assign(getDashboardUrl());
  }
};

const errHandler = (res) => {
  console.error("[error-handler]", res);
};

export const get = async (url, headers) => {
  const [status, error] = connectionHandler();
  if (!status) {
    return error;
  }

  return new Promise(async (resolve, reject) => {
    // await checkExpiry();
    fetch(finalUrl(url), {
      method: "GET",
      headers,
    })
      .then((res) => {
        if (isAuthError(res)) {
          return handleAuthError();
        }
        return res.json();
      })
      .then((res) => {
        if (getPath(res, "error.code", undefined)) {
          reject(getErrorMessage(res));
        } else {
          resolve(res.result ? res.result : res);
        }
      })
      .catch((err) => {
        errHandler(err);
        reject(getErrorMessage(err));
      });
  });
};

export const post = async (url, data = {}, headers, isFormData) => {
  const [status, error] = connectionHandler();
  if (!status) {
    return error;
  }

  return new Promise(async (resolve, reject) => {
    // await checkExpiry();
    fetch(finalUrl(url), {
      method: "POST",
      headers,
      body: isFormData ? data : JSON.stringify(data),
    })
      .then((res) => {
        if (isAuthError(res)) {
          return handleAuthError();
        }
        return res.json();
      })
      .then((res) => {
        if (getPath(res, "error.code", undefined)) {
          reject(getErrorMessage(res));
        } else {
          resolve(res.result ? res.result : res);
        }
      })
      .catch((err) => {
        errHandler(err);
        reject(getErrorMessage(err));
      });
  });
};

export const put = async (url, data = {}, headers) => {
  const [status, error] = connectionHandler();
  if (!status) {
    return error;
  }

  return new Promise(async (resolve, reject) => {
    // await checkExpiry();
    const contentType = headers && headers["Content-Type"];
    fetch(finalUrl(url), {
      method: "PUT",
      headers: contentType && contentType.includes("form-data") ? {} : headers,
      body:
        contentType && contentType.includes("form-data")
          ? data
          : JSON.stringify(data),
    })
      .then((res) => {
        if (isAuthError(res)) {
          return handleAuthError();
        }
        return res.json();
      })
      .then((res) => {
        if (getPath(res, "error.code", undefined)) {
          reject(getErrorMessage(res));
        } else {
          resolve(res.result ? res.result : res);
        }
      })
      .catch((err) => {
        errHandler(err);
        reject(getErrorMessage(err));
      });
  });
};

export const externalPut = async (url, data = {}, headers) => {
  const [status, error] = connectionHandler();
  if (!status) {
    return error;
  }

  return new Promise((resolve, reject) => {
    fetch(finalUrl(url), {
      method: "PUT",
      headers,
      body: data,
    })
      .then((res) => {
        if (isAuthError(res)) {
          return handleAuthError();
        }
        return res.json();
      })
      .then((res) => {
        if (getPath(res, "error.code", undefined)) {
          reject(getErrorMessage(res));
        } else {
          resolve(res.result ? res.result : res);
        }
      })
      .catch((err) => {
        errHandler(err);
        reject(getErrorMessage(err));
      });
  });
};

export const patch = async (url, data = {}, headers) => {
  const [status, error] = connectionHandler();
  if (!status) {
    return error;
  }

  return new Promise(async (resolve, reject) => {
    // await checkExpiry();
    fetch(finalUrl(url), {
      method: "PATCH",
      headers,
      body: JSON.stringify(data),
    })
      .then((res) => {
        if (isAuthError(res)) {
          return handleAuthError();
        }
        return res.json();
      })
      .then((res) => {
        if (getPath(res, "error.code", undefined)) {
          reject(getErrorMessage(res));
        } else {
          resolve(res.result ? res.result : res);
        }
      })
      .catch((err) => {
        errHandler(err);
        reject(getErrorMessage(err));
      });
  });
};

export const deleteCall = async (url, data, headers) => {
  const [status, error] = connectionHandler();
  if (!status) {
    return error;
  }

  return new Promise(async (resolve, reject) => {
    // await checkExpiry();
    fetch(finalUrl(url), {
      method: "DELETE",
      headers,
      body: JSON.stringify(data),
    })
      .then((res) => {
        if (isAuthError(res)) {
          return handleAuthError();
        }
        return res.json();
      })
      .then((res) => {
        if (getPath(res, "error.code", undefined)) {
          reject(getErrorMessage(res));
        } else {
          resolve(res.result ? res.result : res);
        }
      })
      .catch((err) => {
        errHandler(err);
        reject(getErrorMessage(err));
      });
  });
};

export const Api = {
  get: (url, noBaseUrl, headers = _AuthHeaders()) =>
    get(`${noBaseUrl ? url : Config.API_URL + url}`, headers),

  post: (url, data, headers, noBaseUrl, isFormData) =>
    post(
      `${noBaseUrl ? url : Config.API_URL + url}`,
      data,
      { ..._AuthHeaders(isFormData), ...headers },
      isFormData
    ),

  put: (url, data, headers = _AuthHeaders(), noBaseUrl) =>
    put(`${noBaseUrl ? url : Config.API_URL + url}`, data, headers),

  patch: (url, data, headers = _AuthHeaders(), noBaseUrl) =>
    patch(`${noBaseUrl ? url : Config.API_URL + url}`, data, headers),

  deleteCall: (url, data, headers = _AuthHeaders()) =>
    deleteCall(`${Config.API_URL}${url}`, data, headers),

  externalPutCall: (url, data, headers = _AuthHeaders()) =>
    externalPut(url, data, headers),

  download: async (url, noBaseUrl, headers = _AuthHeaders()) => {
      try {
        const response = await fetch(`${noBaseUrl ? url : Config.API_URL + url}`, {
          method: 'GET',
          headers,
        });
  
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
  
        const blob = await response.blob();
        return { blob, headers: response.headers };
      } catch (error) {
        console.error('Fetch error:', error);
        throw error;
      }
    },

  url: () => Config.API_URL,
  headers: () => {
    return _AuthHeaders();
  },
};
